import React, { useCallback, useEffect, useRef, useMemo, useState } from 'react';
import { BackButton } from '@helpcenter/back-button';
import { debounce } from 'lodash';
import { useIntl } from 'react-intl';
import { useRouterActions } from 'react-resource-router';
import type { UpdateSearchAction, ShowSearchAction } from 'state/actions/search';
import withAnalyticsEvents from '@atlaskit/analytics-next/withAnalyticsEvents';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import CrossIcon from '@atlaskit/icon/core/cross';
import SearchIcon from '@atlaskit/icon/core/search';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { Popup } from '@atlaskit/popup/popup';
import { Box } from '@atlaskit/primitives/box';
import { Grid } from '@atlaskit/primitives/grid';
import { Inline } from '@atlaskit/primitives/inline';
import { xcss } from '@atlaskit/primitives/xcss/xcss';
import { media } from '@atlaskit/primitives/responsive/media';
import Textfield from '@atlaskit/textfield/text-field';
import { token } from '@atlaskit/tokens';
import { addParamsToUrl } from '@atlassian/help-center-common-util/history-qs';
import {
	isSearchQueryOverCharacterLimit,
	SEARCH_BAR_HEIGHT,
	SEARCH_QUERY_CHARACTER_LIMIT,
	useQueryParams,
	SEARCH_QUERY_DEBOUNCE_TIME,
} from '../common';
import messages from './messages';
const SEARCH_CHARACTER_LIMIT_ERROR_ICON = 'searchQueryCharacterLimitErrorIcon';
const SEARCH_CHARACTER_LIMIT_ERROR_POPOVER = 'searchQueryCharacterLimitErrorPopover';
const HELP_CENTER_SEARCH_LIMIT_GATE = 'jsm-kb-help-center-search-limit';
const MOBILE_SEARCH_BAR_BREAKPOINT_WIDTH = 768;

const isHelpCenterSearchLimitEnabled = (): boolean =>
	FeatureGates.checkGate(HELP_CENTER_SEARCH_LIMIT_GATE);

const getBackButtonText = (width: number): string | undefined =>
	width < MOBILE_SEARCH_BAR_BREAKPOINT_WIDTH ? '' : undefined;

const fireSearchCharacterLimitAnalytics = (
	createAnalyticsEvent: CreateUIAnalyticsEvent | undefined,
	action: 'clicked' | 'rendered',
	actionSubject: 'button' | 'popover',
	actionSubjectId: string,
) => {
	createAnalyticsEvent?.({
		action,
		actionSubject,
		actionSubjectId,
		analyticsType: 'UI',
	}).fire();
};

export interface Props {
	term: string;
	onChange: ((term: string) => UpdateSearchAction) | ((term: string) => void);
	onClose: () => ShowSearchAction;
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
}

export const SearchBar = (props: Props) => {
	if (isHelpCenterSearchLimitEnabled()) {
		return <SearchBarWithSearchLimit {...props} />;
	}

	return <SearchBarLegacy {...props} />;
};

const SearchBarLegacy = ({ term, onChange, createAnalyticsEvent }: Props) => {
	const { formatMessage } = useIntl();
	const [backButtonText, setBackButtonText] = useState<string>();
	const searchBarRef = useRef(null);
	const [searchQuery, setSearchQuery] = useState(term);
	useEffect(() => {
		let observer: ResizeObserver;
		const currentSearchBarRefRef = searchBarRef.current;
		if (window.ResizeObserver) {
			observer = new window.ResizeObserver((entries) => {
				for (const entry of entries) {
					setBackButtonText(entry.contentRect.width < 768 ? '' : undefined);
				}
			});
			if (currentSearchBarRefRef) {
				observer.observe(currentSearchBarRefRef);
			}
		}
		return () => {
			if (currentSearchBarRefRef) observer.unobserve(currentSearchBarRefRef);
		};
	}, []);
	const debouncedOnChangeMethod = useMemo(
		() =>
			debounce((value) => {
				onChange(value);
			}, SEARCH_QUERY_DEBOUNCE_TIME),
		[onChange],
	);
	const onSearchTermChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setSearchQuery(e.target.value);
			if (FeatureGates.checkGate('debounce_change_in_portal_search')) {
				debouncedOnChangeMethod(e.target.value);
			} else {
				onChange(e.target.value);
			}
			if (createAnalyticsEvent) {
				createAnalyticsEvent({
					analyticsType: 'UI',
					action: 'changed',
					actionSubject: 'searchTermValue',
					actionSubjectId: 'searchTerm',
				}).fire();
			}
		},
		[createAnalyticsEvent, debouncedOnChangeMethod, onChange],
	);
	useEffect(() => {
		if (createAnalyticsEvent) {
			createAnalyticsEvent({
				analyticsType: 'UI',
				action: 'rendered',
				actionSubject: 'searchBarTextField',
				actionSubjectId: 'searchBar',
			}).fire();
		}
	}, [createAnalyticsEvent]);
	const { goBack, push } = useRouterActions();
	const onBackButtonClick = useCallback(() => {
		goBack() as unknown as void;
	}, [goBack]);
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	const queryStringParams = useQueryParams();
	const onCrossIconClick = useCallback(() => {
		setSearchQuery('');
		onChange('');
		push(addParamsToUrl('', { ...queryStringParams, q: '' })) as unknown as void;
	}, [onChange, push, queryStringParams]);
	return (
		<Inline xcss={containerStyles} ref={searchBarRef}>
			<Grid gap="space.200" templateColumns="25fr 50fr 25fr" alignItems="center">
				<Box xcss={backButtonStyles}>
					<BackButton
						text={backButtonText}
						actionSubjectId="closeSearch"
						onClick={onBackButtonClick}
					/>
				</Box>
				<Box xcss={textFieldLegacyStyles}>
					<Textfield
						id="search-bar-input"
						value={searchQuery}
						name={formatMessage(messages.inputLabel)}
						aria-label={formatMessage(messages.inputPlaceholder)}
						onChange={onSearchTermChange}
						placeholder={formatMessage(messages.inputPlaceholder)}
						aria-describedby="dynamic-search-result"
						elemBeforeInput={
							<Box xcss={searchIconStyles}>
								<SearchIcon label="" spacing="spacious" color={token('color.icon', '#42526E')} />
							</Box>
						}
						elemAfterInput={
							<Box
								as="button"
								aria-label={formatMessage(messages.clearLabel)}
								xcss={crossIconStyles}
								onClick={onCrossIconClick}
							>
								<CrossIcon
									label={formatMessage(messages.clearLabel)}
									spacing="spacious"
									color={token('color.icon', '#42526E')}
								/>
							</Box>
						}
						// Disabling existing violations, should be fixed when revisited.

						autoFocus
					/>
				</Box>
				<Box />
			</Grid>
		</Inline>
	);
};

const SearchBarWithSearchLimit = ({ term, onChange, createAnalyticsEvent }: Props) => {
	const { formatMessage } = useIntl();
	const [backButtonText, setBackButtonText] = useState<string | undefined>(() =>
		typeof window === 'undefined' ? undefined : getBackButtonText(window.innerWidth),
	);
	const searchBarRef = useRef<HTMLElement | null>(null);
	const [searchQuery, setSearchQuery] = useState(term);
	const [isCharacterLimitErrorOpen, setIsCharacterLimitErrorOpen] = useState(false);
	const isSearchQueryOverLimit = isSearchQueryOverCharacterLimit(searchQuery);
	useEffect(() => {
		let observer: ResizeObserver | undefined;
		const currentSearchBarRefRef = searchBarRef.current;
		if (currentSearchBarRefRef) {
			setBackButtonText(getBackButtonText(currentSearchBarRefRef.getBoundingClientRect().width));
		}
		if (window.ResizeObserver) {
			observer = new window.ResizeObserver((entries) => {
				for (const entry of entries) {
					setBackButtonText(getBackButtonText(entry.contentRect.width));
				}
			});
			if (currentSearchBarRefRef) {
				observer.observe(currentSearchBarRefRef);
			}
		}
		return () => {
			if (currentSearchBarRefRef) observer?.unobserve(currentSearchBarRefRef);
		};
	}, []);
	const debouncedOnChangeMethod = useMemo(
		() =>
			debounce((value) => {
				onChange(value);
			}, SEARCH_QUERY_DEBOUNCE_TIME),
		[onChange],
	);
	const onSearchTermChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const newSearchQuery = e.target.value;
			const isNewSearchQueryOverLimit = isSearchQueryOverCharacterLimit(newSearchQuery);
			setSearchQuery(newSearchQuery);
			setIsCharacterLimitErrorOpen(false);
			if (isNewSearchQueryOverLimit) {
				return;
			}
			if (FeatureGates.checkGate('debounce_change_in_portal_search')) {
				debouncedOnChangeMethod(newSearchQuery);
			} else {
				onChange(newSearchQuery);
			}
			if (createAnalyticsEvent) {
				createAnalyticsEvent({
					analyticsType: 'UI',
					action: 'changed',
					actionSubject: 'searchTermValue',
					actionSubjectId: 'searchTerm',
				}).fire();
			}
		},
		[createAnalyticsEvent, debouncedOnChangeMethod, onChange],
	);
	useEffect(() => {
		if (createAnalyticsEvent) {
			createAnalyticsEvent({
				analyticsType: 'UI',
				action: 'rendered',
				actionSubject: 'searchBarTextField',
				actionSubjectId: 'searchBar',
			}).fire();
		}
	}, [createAnalyticsEvent]);
	const { goBack, push } = useRouterActions();
	const onBackButtonClick = useCallback(() => {
		goBack() as unknown as void;
	}, [goBack]);
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	const queryStringParams = useQueryParams();
	const onCrossIconClick = useCallback(() => {
		setSearchQuery('');
		setIsCharacterLimitErrorOpen(false);
		onChange('');
		push(addParamsToUrl('', { ...queryStringParams, q: '' })) as unknown as void;
	}, [onChange, push, queryStringParams]);
	const onCharacterLimitErrorIconClick = useCallback(() => {
		fireSearchCharacterLimitAnalytics(
			createAnalyticsEvent,
			'clicked',
			'button',
			SEARCH_CHARACTER_LIMIT_ERROR_ICON,
		);

		const nextIsOpen = !isCharacterLimitErrorOpen;
		setIsCharacterLimitErrorOpen(nextIsOpen);

		if (nextIsOpen) {
			fireSearchCharacterLimitAnalytics(
				createAnalyticsEvent,
				'rendered',
				'popover',
				SEARCH_CHARACTER_LIMIT_ERROR_POPOVER,
			);
		}
	}, [createAnalyticsEvent, isCharacterLimitErrorOpen]);
	return (
		<Inline xcss={containerStyles} ref={searchBarRef}>
			<Grid gap="space.200" templateColumns="auto minmax(0, 1fr) auto" alignItems="center">
				<Box xcss={backButtonStyles}>
					<BackButton
						text={backButtonText}
						actionSubjectId="closeSearch"
						onClick={onBackButtonClick}
						allowEmptyText
					/>
				</Box>
				<Box xcss={textFieldStyles}>
					<Textfield
						id="search-bar-input"
						value={searchQuery}
						name={formatMessage(messages.inputLabel)}
						aria-label={formatMessage(messages.inputPlaceholder)}
						onChange={onSearchTermChange}
						placeholder={formatMessage(messages.inputPlaceholder)}
						aria-describedby="dynamic-search-result"
						isInvalid={isSearchQueryOverLimit}
						elemBeforeInput={
							<Box xcss={searchIconStyles}>
								<SearchIcon label="" spacing="spacious" color={token('color.icon', '#42526E')} />
							</Box>
						}
						elemAfterInput={
							isSearchQueryOverLimit ? (
								<Popup
									isOpen={isCharacterLimitErrorOpen}
									onClose={() => setIsCharacterLimitErrorOpen(false)}
									placement="bottom-end"
									autoFocus={false}
									shouldRenderToParent
									trigger={(triggerProps) => (
										<Box
											{...triggerProps}
											as="button"
											type="button"
											aria-label={formatMessage(messages.characterLimitErrorIconLabel)}
											xcss={searchErrorIconStyles}
											onClick={onCharacterLimitErrorIconClick}
										>
											<StatusErrorIcon
												label=""
												spacing="spacious"
												color={token('color.icon.danger', '#C9372C')}
											/>
										</Box>
									)}
									content={() => (
										<Box xcss={characterLimitErrorStyles} role="alert">
											{formatMessage(messages.characterLimitExceeded, {
												limit: SEARCH_QUERY_CHARACTER_LIMIT,
											})}
										</Box>
									)}
								/>
							) : (
								<Box
									as="button"
									aria-label={formatMessage(messages.clearLabel)}
									xcss={crossIconStyles}
									onClick={onCrossIconClick}
								>
									<CrossIcon
										label={formatMessage(messages.clearLabel)}
										spacing="spacious"
										color={token('color.icon', '#42526E')}
									/>
								</Box>
							)
						}
						// Disabling existing violations, should be fixed when revisited.

						autoFocus
					/>
				</Box>
				<Box />
			</Grid>
		</Inline>
	);
};
const containerStyles = xcss({
	display: 'flex',
	position: 'fixed',
	alignItems: 'center',
	width: '100vw',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Disabled to rollout go/ui-styling-standard tooling, please resolve
	height: SEARCH_BAR_HEIGHT,
	backgroundColor: 'elevation.surface.overlay',
	borderBottomWidth: 'border.width',
	borderBottomStyle: 'solid',
	borderBottomColor: token('color.border', '#EBECF0'),
	zIndex: 'modal',
});
const backButtonStyles = xcss({
	[media.above.xxs]: {
		marginLeft: 'space.050',
	},
	[media.above.xs]: {
		marginLeft: 'space.100',
	},
	[media.above.sm]: {
		marginLeft: 'space.200',
	},
});
const textFieldStyles = xcss({
	height: 'space.500',
	marginLeft: 'space.100',
	marginRight: 'space.100',
	background: 'transparent',
	width: '100%',
	[media.above.xs]: {
		width: '432px',
	},
	[media.above.sm]: {
		width: '692px',
	},
	[media.above.md]: {
		width: '965px',
	},
});
const textFieldLegacyStyles = xcss({
	height: 'space.500',
	marginLeft: 'space.100',
	marginRight: 'space.100',
	background: 'transparent',
	[media.above.xxs]: {
		width: '348px',
	},
	[media.above.xs]: {
		width: '432px',
	},
	[media.above.sm]: {
		width: '692px',
	},
	[media.above.md]: {
		width: '965px',
	},
});
const searchIconStyles = xcss({
	padding: 'space.100',
});
const searchErrorIconStyles = xcss({
	background: 'transparent',
	cursor: 'pointer',
	padding: 'space.100',
});
const crossIconStyles = xcss({
	background: 'transparent',
	cursor: 'pointer',
});
const characterLimitErrorStyles = xcss({
	color: 'color.text',
	paddingBlock: 'space.150',
	paddingInline: 'space.200',
	textAlign: 'right',
});
export default withAnalyticsEvents()(SearchBar);
